// @ts-nocheck
const code = (value) => String(value || '').toUpperCase();
const AVAILABILITY_EXPIRY_MARGIN_SECONDS = 30;
const discoveryScope = (config) =>
  JSON.stringify([
    code(config.operatorCurrency),
    config.quoteContext?.tripId,
    config.quoteContext?.tripKey,
    config.quoteContext?.bookingId,
    config.quoteContext?.bookingKey
  ]);
const sameMoney = (left, right) =>
  left?.currency === right?.currency &&
  left?.chargeAmount === right?.chargeAmount &&
  left?.operatorBookingCredit === right?.operatorBookingCredit;
const unwrap = (response) => {
  if (response?.error) {
    throw Object.assign(
      new Error(response.error.message || response.error.description),
      {
        name: response.error.code || response.error.errorCode,
        details: response.error.details,
        statusCode: response.error.statusCode
      }
    );
  }
  if (!response?.data) throw new Error('Invalid payment response');
  return response.data;
};
const verifiedSelection = (value, intent, operatorCurrency) => {
  const currency = code(value.chargeCurrency || value.currency);
  const foreign = currency !== code(operatorCurrency);
  if (
    !/^[A-Z]{3}$/.test(currency) ||
    code(value.operatorCurrency || operatorCurrency) !==
      code(operatorCurrency) ||
    !Number.isFinite(value.chargeAmount) ||
    value.chargeAmount <= 0 ||
    !Number.isFinite(value.operatorBookingCredit) ||
    value.operatorBookingCredit <= 0 ||
    !intent.paymentIntentId ||
    !intent.clientSecret ||
    (value.chargeAmountMinor !== undefined &&
      value.chargeAmountMinor !== Math.round(value.chargeAmount * 100)) ||
    (foreign &&
      (!value.fxQuoteId?.startsWith('fxq_') ||
        !Number.isFinite(value.lockedRate) ||
        value.lockedRate <= 0 ||
        !Number.isFinite(value.expiresAt) ||
        value.expiresAt <= Date.now() / 1000))
  ) {
    throw new Error('Invalid verified payment');
  }
  return { ...value, currency };
};

/** One owner for cancellation, replacement and confirmed checkout money. */
export class CheckoutCurrencyController {
  constructor(api, uuid = () => crypto.randomUUID()) {
    this.api = api;
    this.uuid = uuid;
    this.config = {};
    this.listeners = new Set();
    this.state = {
      availability: null,
      selection: null,
      intent: null,
      managed: false,
      loading: false,
      error: null,
      pendingSelection: null,
      reconfirmationPending: false,
      invalidated: false
    };
    this.queue = Promise.resolve();
    this.attempt = null;
    this.discovery = null;
    this.pendingIntentOperations = 0;
    this.paymentLock = false;
    this.completed = false;
    this.unresolved = false;
    this.reconciliationRequired = false;
    this.disposed = false;
    this.version = 0;
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  emit(patch) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener(this.snapshot()));
  }
  snapshot() {
    // Background availability must not lock early-step navigation. Once payment
    // is active, keep its controls blocked until discovery settles. Intent work
    // owns a separate lock that discovery completion must never release.
    const availabilityLoading = Boolean(this.discovery?.pending);
    const loading = Boolean(
      this.state.loading ||
      (this.config.active && availabilityLoading) ||
      this.pendingIntentOperations ||
      this.timer ||
      this.updateTimer
    );
    const currentMoney = this.state.pendingSelection || this.state.selection;
    const creditMismatch = Boolean(
      currentMoney &&
      (!Number.isFinite(this.config.operatorBookingCredit) ||
        Math.round(currentMoney.operatorBookingCredit * 100) !==
          Math.round(this.config.operatorBookingCredit * 100))
    );
    const expired = Boolean(
      currentMoney?.fxQuoteId && currentMoney.expiresAt <= Date.now() / 1000
    );
    const unavailable = Boolean(
      this.paymentLock ||
      this.config.processing ||
      this.completed ||
      this.reconciliationRequired ||
      !this.state.intent?.paymentIntentId ||
      !this.state.intent?.clientSecret ||
      loading ||
      this.state.error ||
      this.state.invalidated ||
      creditMismatch ||
      expired
    );
    const canConfirmReconfirmation = Boolean(
      !unavailable &&
      !this.attempt &&
      this.state.reconfirmationPending &&
      this.state.pendingSelection?.paymentIntentId ===
        this.state.intent?.paymentIntentId
    );
    const blocked = Boolean(
      unavailable ||
      this.state.reconfirmationPending ||
      (this.state.managed && !this.state.selection)
    );
    return {
      ...this.state,
      availabilityLoading,
      loading,
      quoteInvalidated:
        this.state.error?.name === 'FX_QUOTE_INVALID_OR_EXPIRED',
      completed: this.completed,
      unresolved: this.unresolved,
      reconciliationRequired: this.reconciliationRequired,
      creditMismatch,
      blocked,
      paymentBlocked: blocked && !canConfirmReconfirmation
    };
  }
  configure(config) {
    const footprint = JSON.stringify([
      config.checkout,
      config.operatorAmount,
      config.operatorBookingCredit,
      config.paymentMethod || 'card'
    ]);
    const changed =
      this.footprint !== undefined && this.footprint !== footprint;
    this.config = config;
    this.footprint = footprint;
    if (config.active === false && this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
      this.emit({
        loading: Boolean(this.attempt || this.pendingIntentOperations)
      });
    }
    if (this.discovery && this.discovery.scope !== discoveryScope(config)) {
      this.discovery = null;
      this.emit({ availability: null });
    }
    if (!this.state.managed) this.state.intent = config.initialIntent || null;
    if (changed && this.state.managed && !this.completed) {
      this.emit({ invalidated: true });
    }
    if (
      this.state.invalidated &&
      this.state.managed &&
      config.active &&
      !this.completed &&
      !this.paymentLock &&
      !config.processing &&
      !this.attempt
    ) {
      this.scheduleUpdate();
    }
  }
  async discover(force = false) {
    if (
      !this.config.enabled ||
      !(this.config.active || this.config.prefetch) ||
      this.disposed
    )
      return;
    const context = this.config.quoteContext;
    if (
      !(context?.tripId && context.tripKey) &&
      !(context?.bookingId && context.bookingKey)
    )
      return;
    const scope = discoveryScope(this.config);
    const previous = this.discovery?.scope === scope ? this.discovery : null;
    // The cache is private to this checkout owner. Availability is a display
    // reference; the backend still creates and validates a fresh PI-bound quote.
    if (previous?.pending) return previous.promise;
    const now = Date.now() / 1000;
    if (
      !force &&
      previous?.value?.expiresAt > now + AVAILABILITY_EXPIRY_MARGIN_SECONDS
    )
      return previous.value;
    const retryPrevious =
      !force &&
      previous &&
      !previous.value &&
      previous.startedAt + 3600 > now + AVAILABILITY_EXPIRY_MARGIN_SECONDS;
    const discovery = {
      scope,
      operatorCurrency: code(this.config.operatorCurrency),
      requestId: retryPrevious ? previous.requestId : this.uuid(),
      startedAt: retryPrevious ? previous.startedAt : now,
      value: null,
      pending: true,
      promise: null
    };
    this.discovery = discovery;
    this.emit({});
    discovery.promise = this.api
      .getFxQuote({ ...context, requestId: discovery.requestId })
      .then(unwrap)
      .then((availability) => {
        if (this.disposed || this.discovery !== discovery) return null;
        if (
          code(availability.operatorCurrency) !== discovery.operatorCurrency ||
          !availability.quoteId?.startsWith('fxq_') ||
          !availability.rates ||
          !Number.isFinite(availability.expiresAt) ||
          availability.expiresAt <= Date.now() / 1000
        )
          throw new Error('Invalid currency availability');
        discovery.value = availability;
        this.emit({ availability, error: null });
        return availability;
      })
      .catch((error) => {
        if (!this.disposed && this.discovery === discovery)
          this.emit({
            availability: null,
            error: this.state.managed ? error : null
          });
        return null;
      })
      .finally(() => {
        discovery.pending = false;
        if (!this.disposed && this.discovery === discovery) this.emit({});
      });
    return discovery.promise;
  }
  enqueue(operation) {
    this.queue = this.queue.catch(() => {}).then(operation);
    return this.queue;
  }
  publish(selection, intent, requireConfirmation) {
    if (this.disposed) return;
    this.emit({
      managed: true,
      intent,
      error: null,
      invalidated: false,
      ...(requireConfirmation
        ? { pendingSelection: selection, reconfirmationPending: true }
        : { selection, pendingSelection: null, reconfirmationPending: false })
    });
    this.config.onIntentChange?.({
      ...intent,
      selection,
      pending: requireConfirmation
    });
  }
  requestCurrency(currency, options = {}) {
    if (
      this.paymentLock ||
      this.config.processing ||
      this.completed ||
      this.reconciliationRequired
    )
      return Promise.resolve(false);
    const target = code(currency);
    if (
      !options.refresh &&
      target ===
        code(
          this.state.pendingSelection?.currency ||
            this.state.selection?.currency ||
            this.config.operatorCurrency
        )
    )
      return Promise.resolve(false);
    if (target !== code(this.config.operatorCurrency) && !this.config.enabled)
      return Promise.resolve(false);
    if (this.config.paymentMethod === 'us_bank_account' && target !== 'USD')
      return Promise.resolve(false);
    // Freeze one operation until its outcome is known. A retry uses its exact
    // request and attempt ID rather than creating a second payment.
    if (this.attempt) return Promise.resolve(false);
    clearTimeout(this.timer);
    clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.pendingResolve?.(false);
    this.emit({ managed: true, loading: true, invalidated: true, error: null });
    return new Promise((resolve) => {
      this.pendingResolve = resolve;
      this.timer = setTimeout(() => {
        this.pendingResolve = null;
        this.timer = null;
        resolve(this.enqueue(() => this.replace(target, options.refresh)));
      }, 0);
    });
  }
  async replace(target, refreshing) {
    if (
      this.disposed ||
      this.config.active === false ||
      this.paymentLock ||
      this.config.processing ||
      this.completed
    ) {
      this.emit({ loading: false });
      return false;
    }
    const previous = this.state.intent || this.config.initialIntent;
    if (!previous?.paymentIntentId || !previous.clientSecret) {
      this.emit({ loading: false, error: new Error('Payment is not ready') });
      return false;
    }
    const body = {
      checkout: structuredClone(this.config.checkout),
      chargeCurrency: target,
      requestId: this.uuid(),
      paymentIntentId: previous.paymentIntentId,
      clientSecret: previous.clientSecret,
      paymentMethods: [this.config.paymentMethod || 'card']
    };
    this.attempt = {
      kind: 'replace',
      footprint: this.footprint,
      previous,
      body,
      refreshing
    };
    return this.runAttempt();
  }
  async runAttempt() {
    const attempt = this.attempt;
    if (!attempt || this.completed || this.paymentLock) return false;
    this.pendingIntentOperations += 1;
    this.emit({ loading: true, error: null });
    try {
      let response;
      if (attempt.kind === 'replace') {
        if (
          attempt.body.chargeCurrency !== code(this.config.operatorCurrency) &&
          !attempt.body.fxQuoteId
        ) {
          let availability = await this.discover(Boolean(attempt.refreshing));
          if (
            availability &&
            !availability.rates?.[attempt.body.chargeCurrency] &&
            !attempt.refreshing
          )
            availability = await this.discover(true);
          if (
            !availability?.quoteId ||
            !Number.isFinite(
              availability.rates?.[attempt.body.chargeCurrency]?.rate
            ) ||
            availability.rates[attempt.body.chargeCurrency].rate <= 0
          )
            throw new Error('Currency unavailable');
          attempt.body.fxQuoteId = availability.quoteId;
        }
        if (this.disposed) return false;
        // The backend validates, cancels and creates the canonical successor.
        // An ambiguous response may mean cancellation or creation completed;
        // retain this exact attempt and block payment until it is reconciled.
        this.emit({ managed: true, selection: null, invalidated: true });
        try {
          response = unwrap(await this.api.replacePaymentIntent(attempt.body));
        } catch (error) {
          if (
            error.name !== 'FX_QUOTE_INVALID_OR_EXPIRED' ||
            attempt.referenceRetried
          )
            throw error;
          // Stripe may invalidate a reference before its advertised expiry.
          // Refresh only that reference and retry the same canonical successor;
          // never change the payment request ID or cancel a second intent.
          const availability = await this.discover(true);
          if (this.disposed) return false;
          const rate = availability?.rates?.[attempt.body.chargeCurrency]?.rate;
          if (!Number.isFinite(rate) || rate <= 0) throw error;
          attempt.body.fxQuoteId = availability.quoteId;
          attempt.referenceRetried = true;
          response = unwrap(await this.api.replacePaymentIntent(attempt.body));
        }
      } else {
        response = unwrap(
          await this.api.updatePaymentIntent(
            attempt.intent.paymentIntentId,
            attempt.body
          )
        );
      }
      const intent = {
        paymentIntentId:
          response.paymentIntentId || attempt.intent?.paymentIntentId,
        clientSecret:
          response.clientSecret ||
          response.client_secret ||
          attempt.intent?.clientSecret
      };
      const selection = verifiedSelection(
        response,
        intent,
        this.config.operatorCurrency
      );
      if (this.disposed) {
        await this.api.cancelPaymentIntent(intent).catch(() => {});
        this.attempt = null;
        return false;
      }
      const inputChanged = attempt.footprint !== this.footprint;
      const needsConfirmation = Boolean(
        inputChanged ||
        attempt.refreshing ||
        (attempt.kind === 'update' &&
          !sameMoney(this.state.selection, selection))
      );
      this.attempt = null;
      this.publish(selection, intent, needsConfirmation);
      if (inputChanged) {
        this.emit({ invalidated: true });
        this.scheduleUpdate();
      }
      return true;
    } catch (error) {
      this.emit({ error, invalidated: true });
      // Preserve the canonical successor attempt even for provider 4xx errors.
      // A previous cancellation is irreversible; changing the attempt would
      // risk a second intent after an ambiguous provider response.
      if (error.details?.paymentIntentStatus) {
        this.reconciliationRequired = true;
        this.attempt = null;
      } else if (
        attempt.kind === 'update' &&
        error.name === 'FX_QUOTE_INVALID_OR_EXPIRED'
      ) {
        // The backend rejects an expired update quote before mutation. Its
        // existing intent can be safely canceled for an explicit fresh lock.
        this.attempt = null;
      }
      if (error.name === 'FX_RECONFIRM_REQUIRED' && error.details) {
        this.emit({
          pendingSelection: {
            ...error.details,
            currency: code(error.details.chargeCurrency)
          },
          reconfirmationPending: true
        });
      }
      if (this.disposed) return false;
      return false;
    } finally {
      this.pendingIntentOperations -= 1;
      this.emit({ loading: false });
    }
  }
  scheduleUpdate() {
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => {
      this.updateTimer = null;
      this.update();
    }, 400);
    this.emit({ loading: true });
  }
  update() {
    clearTimeout(this.updateTimer);
    this.updateTimer = null;
    if (
      this.attempt ||
      this.config.active === false ||
      this.paymentLock ||
      this.config.processing ||
      this.completed ||
      this.disposed
    ) {
      this.emit({ loading: false });
      return Promise.resolve(false);
    }
    const selection = this.state.pendingSelection || this.state.selection;
    if (
      !selection ||
      (selection.fxQuoteId &&
        (!this.config.enabled || selection.expiresAt <= Date.now() / 1000))
    ) {
      this.emit({ invalidated: true, loading: false });
      return Promise.resolve(false);
    }
    const intent = this.state.intent;
    this.attempt = {
      kind: 'update',
      footprint: this.footprint,
      intent,
      body: {
        checkout: structuredClone(this.config.checkout),
        chargeCurrency: selection.currency,
        ...(selection.fxQuoteId ? { fxQuoteId: selection.fxQuoteId } : {}),
        requestId: this.uuid(),
        clientSecret: intent.clientSecret,
        paymentMethods: [this.config.paymentMethod || 'card']
      }
    };
    this.emit({ loading: true });
    return this.enqueue(() => this.runAttempt());
  }
  refresh() {
    return this.requestCurrency(
      this.state.selection?.currency ||
        this.state.pendingSelection?.currency ||
        this.config.operatorCurrency,
      { refresh: true }
    );
  }
  async confirmReconfirmation() {
    if (
      this.paymentLock ||
      this.config.processing ||
      this.completed ||
      !this.state.pendingSelection
    )
      return false;
    if (this.snapshot().creditMismatch) return false;
    if (this.state.error?.name === 'FX_RECONFIRM_REQUIRED') {
      if (!this.attempt) return false;
      this.attempt.body.amount = this.state.pendingSelection.chargeAmount;
      const success = await this.enqueue(() => this.runAttempt());
      if (!success) return false;
      if (!this.state.pendingSelection)
        return Boolean(this.state.selection && !this.snapshot().creditMismatch);
    }
    const selection = this.state.pendingSelection;
    if (
      !selection ||
      (selection.fxQuoteId && selection.expiresAt <= Date.now() / 1000)
    )
      return false;
    this.emit({
      selection,
      pendingSelection: null,
      reconfirmationPending: false,
      invalidated: false,
      error: null
    });
    this.config.onIntentChange?.({
      ...this.state.intent,
      selection,
      pending: false
    });
    return true;
  }
  retry() {
    if (this.reconciliationRequired) return Promise.resolve(false);
    if (this.state.error?.name === 'PAYMENT_INTENT_CANCELED')
      return this.refresh();
    if (this.state.error?.name === 'FX_RECONFIRM_REQUIRED' && this.attempt) {
      // Retrying prepares the verified intent at the displayed amount. It does
      // not accept or charge that amount; the payment CTA still must do so.
      this.attempt.body.amount = this.state.pendingSelection?.chargeAmount;
      this.attempt.refreshing = true;
    }
    return this.attempt
      ? this.enqueue(() => this.runAttempt())
      : this.state.managed
        ? this.update()
        : this.discover(true);
  }
  beginPayment(disclosedSelection) {
    if (this.snapshot().paymentBlocked || !this.config.active || this.disposed)
      return false;
    const pending = this.state.pendingSelection;
    if (this.state.reconfirmationPending) {
      if (
        !sameMoney(disclosedSelection, pending) ||
        disclosedSelection.paymentIntentId !== pending.paymentIntentId ||
        disclosedSelection.fxQuoteId !== pending.fxQuoteId ||
        disclosedSelection.expiresAt !== pending.expiresAt ||
        disclosedSelection.lockedRate !== pending.lockedRate
      )
        return false;
      this.state = {
        ...this.state,
        selection: pending,
        pendingSelection: null,
        reconfirmationPending: false
      };
    }
    this.paymentLock = true;
    this.emit({});
    if (pending)
      this.config.onIntentChange?.({
        ...this.state.intent,
        selection: pending,
        pending: false
      });
    return true;
  }
  endPayment({
    completed = false,
    unresolved = false,
    invalidated = false
  } = {}) {
    this.completed ||= completed;
    this.unresolved = !this.completed && unresolved;
    this.paymentLock = this.completed || this.unresolved;
    this.emit(
      invalidated
        ? {
            invalidated: true,
            managed: true,
            error: Object.assign(new Error('Payment intent canceled'), {
              name: 'PAYMENT_INTENT_CANCELED'
            })
          }
        : {}
    );
  }
  reset() {
    if (
      this.paymentLock ||
      this.config.processing ||
      this.completed ||
      this.reconciliationRequired
    )
      return Promise.resolve(false);
    clearTimeout(this.timer);
    this.timer = null;
    this.pendingResolve?.(false);
    clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.emit({ loading: false });
    return this.enqueue(async () => {
      if (this.attempt) {
        if (!(await this.runAttempt())) return false;
      }
      if (!this.state.managed) {
        this.discovery = null;
        this.emit({ availability: null });
        return true;
      }
      try {
        const canceled = unwrap(
          await this.api.cancelPaymentIntent(this.state.intent)
        );
        if (canceled !== true) return false;
        this.emit({
          selection: null,
          pendingSelection: null,
          intent: null,
          managed: false,
          invalidated: false,
          error: null,
          reconfirmationPending: false
        });
        this.config.onIntentChange?.(null);
        this.discovery = null;
        this.emit({ availability: null });
        return true;
      } catch (error) {
        this.emit({ error, invalidated: true });
        return false;
      }
    });
  }
  dispose() {
    clearTimeout(this.timer);
    this.timer = null;
    this.pendingResolve?.(false);
    clearTimeout(this.updateTimer);
    this.updateTimer = null;
    this.disposed = true;
    if (
      this.state.managed &&
      !this.paymentLock &&
      !this.completed &&
      !this.attempt
    )
      this.api.cancelPaymentIntent(this.state.intent).catch(() => {});
  }
}
